/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import FinancialIntegrityCenter from '../components/FinancialIntegrityCenter';   // v3.18-1312
import { 
  ShieldCheck, 
  ShieldAlert, 
  Search, 
  Wrench, 
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Database,
  DollarSign,
  Users,
  FileWarning,
  Loader2,
  Key,
  UserCheck,
  Eye,
  EyeOff,
  Mail,
  MessageSquare,
  Send,
  Wifi,
  WifiOff,
  TrendingUp,
  ArrowRightLeft,
  Trash2,
  ListFilter
} from 'lucide-react';

export default function DataIntegrityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [auditResult, setAuditResult] = useState(null);
  const [fixResult, setFixResult] = useState(null);
  const [postFixAudit, setPostFixAudit] = useState(null);
  const [isAuditing, setIsAuditing] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [error, setError] = useState(null);
  
  // User management state
  const [userEmail, setUserEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userVerifyResult, setUserVerifyResult] = useState(null);
  const [passwordResetResult, setPasswordResetResult] = useState(null);
  const [isVerifyingUser, setIsVerifyingUser] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [userError, setUserError] = useState(null);
  
  // Unfund deals state (v3.14-82)
  const [fundedDeals, setFundedDeals] = useState(null);
  const [selectedDeals, setSelectedDeals] = useState([]);

  // v3.21-1687 · Hub version SSOT — reads from version.json (same source
  // as DashboardLayout / VersionRefreshWatcher).  Removes the previously
  // hardcoded ``v3.15-1043`` string from the Admin Only badge.
  const [hubVersion, setHubVersion] = useState('');
  useEffect(() => {
    let alive = true;
    fetch('/version.json', { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (alive && j?.version) setHubVersion(j.version); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const [isLoadingFunded, setIsLoadingFunded] = useState(false);
  const [isUnfunding, setIsUnfunding] = useState(false);
  const [unfundResult, setUnfundResult] = useState(null);
  const [unfundError, setUnfundError] = useState(null);
  const [unfundNames, setUnfundNames] = useState('');
  
  // Backfill state (v3.14-84)
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillError, setBackfillError] = useState(null);
  
  // v3.14-86: Commission audit state
  const [commissionAudit, setCommissionAudit] = useState(null);
  const [isAuditingCommissions, setIsAuditingCommissions] = useState(false);
  const [isFixingCommissions, setIsFixingCommissions] = useState(false);
  const [commissionFixResult, setCommissionFixResult] = useState(null);
  const [commissionError, setCommissionError] = useState(null);
  
  // v3.14-87: Financial integrity audit state
  const [financialAudit, setFinancialAudit] = useState(null);
  const [isAuditingFinancials, setIsAuditingFinancials] = useState(false);
  const [isSyncingCommissions, setIsSyncingCommissions] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [financialError, setFinancialError] = useState(null);
  
  // v3.14-85: Communications test state
  const [commsStatus, setCommsStatus] = useState(null);
  const [isLoadingCommsStatus, setIsLoadingCommsStatus] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [smsTestResult, setSmsTestResult] = useState(null);
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [isSendingTestSms, setIsSendingTestSms] = useState(false);
  const [commsError, setCommsError] = useState(null);

  // v3.14-92: Client Management state
  const [clientList, setClientList] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientPage, setClientPage] = useState(1);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [deletePreview, setDeletePreview] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [clientError, setClientError] = useState(null);

  // v3.14-101: User Management state
  const [userList, setUserList] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userActionResult, setUserActionResult] = useState(null);
  const [isPerformingUserAction, setIsPerformingUserAction] = useState(false);

  // v3.14-304: Test Data Remover state
  const [testDataPreview, setTestDataPreview] = useState(null);
  const [isLoadingTestData, setIsLoadingTestData] = useState(false);
  const [isDeletingTestData, setIsDeletingTestData] = useState(false);
  const [testDataConfirmation, setTestDataConfirmation] = useState('');
  const [testDataResult, setTestDataResult] = useState(null);
  const [testDataError, setTestDataError] = useState(null);
  const [selectedTestIds, setSelectedTestIds] = useState([]);

  // Duplicate Finder state
  const [duplicates, setDuplicates] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isDeletingDup, setIsDeletingDup] = useState(null);
  const [dupError, setDupError] = useState(null);

  const scanDuplicates = async () => {
    setIsScanning(true);
    setDupError(null);
    try {
      const res = await api.get('/admin/find-duplicates');
      setDuplicates(res.data);
    } catch (err) {
      setDupError(err.response?.data?.detail || 'Failed to scan');
    } finally {
      setIsScanning(false);
    }
  };

  const deleteDuplicateCompany = async (companyId, mergeInto) => {
    setIsDeletingDup(companyId);
    try {
      const params = mergeInto ? `?merge_into=${mergeInto}` : '';
      await api.delete(`/admin/duplicates/company/${companyId}${params}`);
      scanDuplicates();
    } catch (err) {
      setDupError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeletingDup(null);
    }
  };

  const deleteDuplicateClient = async (clientId) => {
    setIsDeletingDup(clientId);
    try {
      await api.delete(`/admin/duplicates/client/${clientId}`);
      scanDuplicates();
    } catch (err) {
      setDupError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeletingDup(null);
    }
  };

  const deleteDuplicateUser = async (userId) => {
    setIsDeletingDup(userId);
    try {
      await api.delete(`/admin/duplicates/user/${userId}`);
      scanDuplicates();
    } catch (err) {
      setDupError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeletingDup(null);
    }
  };

  // Security check - Admin only
  if (user?.role !== 'ptc_admin') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Alert variant="destructive" className="max-w-md">
          <ShieldAlert className="h-5 w-5" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            This page is only accessible to PTC Administrators.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const runAudit = async () => {
    setIsAuditing(true);
    setError(null);
    setAuditResult(null);
    setFixResult(null);
    setPostFixAudit(null);
    
    try {
      const response = await api.post('/admin/audit-funding-status');
      setAuditResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to run audit');
    } finally {
      setIsAuditing(false);
    }
  };

  const runFix = async () => {
    // v3.21-1688 · Bulk fix path retired.  This diagnostic surface no
    // longer writes to financial fields.  Retained as a no-op so the
    // caller signature remains stable for any lingering references.
    setError('Bulk Fix has been retired in v3.21-1688. Corrections must occur through the authoritative funding workflow.');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  // User management functions
  const verifyUser = async () => {
    if (!userEmail.trim()) {
      setUserError('Please enter an email address');
      return;
    }
    setIsVerifyingUser(true);
    setUserError(null);
    setUserVerifyResult(null);
    setPasswordResetResult(null);
    
    try {
      const response = await api.get(`/admin/verify-user/${encodeURIComponent(userEmail.trim())}`);
      setUserVerifyResult(response.data);
    } catch (err) {
      setUserError(err.response?.data?.detail || 'Failed to verify user');
    } finally {
      setIsVerifyingUser(false);
    }
  };

  const resetPassword = async () => {
    if (!userEmail.trim()) {
      setUserError('Please enter an email address');
      return;
    }
    if (!newPassword.trim() || newPassword.length < 6) {
      setUserError('Password must be at least 6 characters');
      return;
    }
    setIsResettingPassword(true);
    setUserError(null);
    
    try {
      const response = await api.post('/admin/reset-user-password', {
        email: userEmail.trim(),
        new_password: newPassword.trim()
      });
      setPasswordResetResult(response.data);
      setNewPassword('');
    } catch (err) {
      setUserError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Unfund deals functions (v3.14-82)
  const loadFundedDeals = async () => {
    setIsLoadingFunded(true);
    setUnfundError(null);
    setFundedDeals(null);
    setSelectedDeals([]);
    setUnfundResult(null);
    
    try {
      const response = await api.get('/admin/list-funded-deals');
      setFundedDeals(response.data);
    } catch (err) {
      setUnfundError(err.response?.data?.detail || 'Failed to load funded deals');
    } finally {
      setIsLoadingFunded(false);
    }
  };

  const toggleDealSelection = (dealId) => {
    setSelectedDeals(prev => 
      prev.includes(dealId) 
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  const selectAllDeals = () => {
    if (fundedDeals?.funded_deals) {
      const allIds = fundedDeals.funded_deals.map(d => d.id);
      setSelectedDeals(prev => prev.length === allIds.length ? [] : allIds);
    }
  };

  const unfundSelectedDeals = async () => {
    if (selectedDeals.length === 0) {
      setUnfundError('Please select at least one deal to unfund');
      return;
    }
    setIsUnfunding(true);
    setUnfundError(null);
    
    try {
      const response = await api.post('/admin/unfund-deals', {
        client_ids: selectedDeals
      });
      setUnfundResult(response.data);
      setSelectedDeals([]);
      // Reload the list
      await loadFundedDeals();
    } catch (err) {
      setUnfundError(err.response?.data?.detail || 'Failed to unfund deals');
    } finally {
      setIsUnfunding(false);
    }
  };

  const unfundByNames = async () => {
    if (!unfundNames.trim()) {
      setUnfundError('Please enter names (comma-separated)');
      return;
    }
    setIsUnfunding(true);
    setUnfundError(null);
    
    const names = unfundNames.split(',').map(n => n.trim()).filter(n => n);
    
    try {
      const response = await api.post('/admin/unfund-by-name', names);
      setUnfundResult(response.data);
      setUnfundNames('');
      // Reload the list
      if (fundedDeals) {
        await loadFundedDeals();
      }
    } catch (err) {
      setUnfundError(err.response?.data?.detail || 'Failed to unfund deals');
    } finally {
      setIsUnfunding(false);
    }
  };

  // Backfill function (v3.14-84)
  const runBackfill = async () => {
    setIsBackfilling(true);
    setBackfillError(null);
    setBackfillResult(null);
    
    try {
      const response = await api.post('/admin/backfill-client-data');
      setBackfillResult(response.data);
    } catch (err) {
      setBackfillError(err.response?.data?.detail || 'Failed to backfill data');
    } finally {
      setIsBackfilling(false);
    }
  };

  // v3.14-92: Client Management functions
  const loadClients = async (page = 1, search = '') => {
    setIsLoadingClients(true);
    setClientError(null);
    
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (search.trim()) params.append('search', search.trim());
      
      const response = await api.get(`/admin/list-all-clients?${params}`);
      setClientList(response.data);
      setClientPage(page);
    } catch (err) {
      setClientError(err.response?.data?.detail || 'Failed to load clients');
    } finally {
      setIsLoadingClients(false);
    }
  };

  const handleClientSelect = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
    // Clear any previous preview/result when selection changes
    setDeletePreview(null);
    setDeleteResult(null);
  };

  const handleSelectAll = () => {
    if (!clientList?.clients) return;
    const allIds = clientList.clients.map(c => c.id);
    setSelectedClients(prev => 
      prev.length === allIds.length ? [] : allIds
    );
    setDeletePreview(null);
    setDeleteResult(null);
  };

  const previewDelete = async () => {
    if (selectedClients.length === 0) {
      setClientError('Select at least one client to delete');
      return;
    }
    setIsDeleting(true);
    setClientError(null);
    setDeleteResult(null);
    
    try {
      const response = await api.post('/admin/delete-clients', {
        client_ids: selectedClients,
        confirm_deletion: false  // Preview only
      });
      setDeletePreview(response.data);
    } catch (err) {
      setClientError(err.response?.data?.detail || 'Failed to preview deletion');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDelete = async () => {
    if (selectedClients.length === 0) return;
    setIsDeleting(true);
    setClientError(null);
    
    try {
      const response = await api.post('/admin/delete-clients', {
        client_ids: selectedClients,
        confirm_deletion: true  // Actual deletion
      });
      setDeleteResult(response.data);
      setDeletePreview(null);
      setSelectedClients([]);
      // Reload list
      loadClients(clientPage, clientSearch);
    } catch (err) {
      setClientError(err.response?.data?.detail || 'Failed to delete clients');
    } finally {
      setIsDeleting(false);
    }
  };

  // v3.14-101: User Management functions
  const loadUsers = async (includeInactive = true) => {
    setIsLoadingUsers(true);
    setUserError(null);
    
    try {
      const response = await api.get('/users', { params: { include_inactive: includeInactive } });
      setUserList(response.data);
    } catch (err) {
      setUserError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleUserAction = async (userId, action) => {
    setIsPerformingUserAction(true);
    setUserError(null);
    setUserActionResult(null);
    
    try {
      let endpoint;
      switch (action) {
        case 'deactivate':
          endpoint = `/users/${userId}/deactivate`;
          break;
        case 'archive':
          endpoint = `/users/${userId}/archive`;
          break;
        case 'reactivate':
          endpoint = `/users/${userId}/reactivate`;
          break;
        case 'delete':
          endpoint = `/users/${userId}/permanent?confirm=true`;
          break;
        default:
          throw new Error('Invalid action');
      }
      
      const method = action === 'delete' ? 'delete' : 'post';
      const response = await api[method](endpoint);
      setUserActionResult(response.data);
      
      // Reload user list
      loadUsers(true);
    } catch (err) {
      setUserError(err.response?.data?.detail || `Failed to ${action} user`);
    } finally {
      setIsPerformingUserAction(false);
    }
  };

  const isClean = auditResult?.audit_summary?.data_integrity_status === 'CLEAN';
  const hasIssues = auditResult?.audit_summary?.incorrectly_marked_as_funded > 0;

  return (
    <div className="space-y-6 p-6" data-testid="data-integrity-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-600" />
            Admin Control Panel
          </h1>
          <p className="text-slate-600 mt-1">
            Data integrity, user management, and system administration
          </p>
        </div>
        {/* v3.21-1687 · Version label now reads from version.json SSOT
            instead of the historical hardcoded ``v3.15-1043`` string.
            The visible label mirrors the current Hub release. */}
        <Badge variant="outline" className="text-xs" data-testid="admin-panel-version">
          Admin Only | {hubVersion || 'loading…'}
        </Badge>
      </div>

      <Tabs defaultValue="data-integrity" className="w-full">
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="duplicates" className="flex items-center gap-2 text-xs">
            <ListFilter className="h-4 w-4" />
            Duplicates
          </TabsTrigger>
          <TabsTrigger value="data-integrity" className="flex items-center gap-2 text-xs">
            <Database className="h-4 w-4" />
            Funding
          </TabsTrigger>
          <TabsTrigger value="unfund-deals" className="flex items-center gap-2 text-xs">
            <XCircle className="h-4 w-4" />
            Unfund
          </TabsTrigger>
          <TabsTrigger value="commission-audit" className="flex items-center gap-2 text-xs">
            <DollarSign className="h-4 w-4" />
            Commissions
          </TabsTrigger>
          <TabsTrigger value="financial-audit" className="flex items-center gap-2 text-xs">
            <TrendingUp className="h-4 w-4" />
            Financials
          </TabsTrigger>
          <TabsTrigger value="backfill" className="flex items-center gap-2 text-xs">
            <RefreshCw className="h-4 w-4" />
            Backfill
          </TabsTrigger>
          <TabsTrigger value="communications" className="flex items-center gap-2 text-xs">
            <Mail className="h-4 w-4" />
            Comms
          </TabsTrigger>
          <TabsTrigger value="client-management" className="flex items-center gap-2 text-xs">
            <Trash2 className="h-4 w-4" />
            Clients
          </TabsTrigger>
          <TabsTrigger value="user-management" className="flex items-center gap-2 text-xs">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="test-data" className="flex items-center gap-2 text-xs" data-testid="tab-test-data">
            <Trash2 className="h-4 w-4" />
            Test Data
          </TabsTrigger>
        </TabsList>


        {/* DUPLICATES TAB */}
        <TabsContent value="duplicates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ListFilter className="h-5 w-5" />
                Duplicate Finder
              </CardTitle>
              <CardDescription>
                Scan for duplicate companies, clients, and users. Delete extras and optionally merge data into a keeper record.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={scanDuplicates}
                disabled={isScanning}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="scan-duplicates-btn"
              >
                {isScanning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                {isScanning ? 'Scanning...' : 'Scan for Duplicates'}
              </Button>

              {dupError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{dupError}</AlertDescription>
                </Alert>
              )}

              {duplicates && duplicates.duplicate_groups === 0 && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">All Clean</AlertTitle>
                  <AlertDescription className="text-green-700">No duplicate records found across companies, clients, or users.</AlertDescription>
                </Alert>
              )}

              {/* Company Duplicates */}
              {duplicates?.results?.companies?.length > 0 && (
                <div className="space-y-3" data-testid="company-duplicates">
                  <h3 className="text-sm font-semibold flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                    Duplicate Companies ({duplicates.results.companies.length} groups)
                  </h3>
                  {duplicates.results.companies.map((group) => {
                    const sorted = [...group.items].sort((a, b) => (b.client_count + b.user_count) - (a.client_count + a.user_count));
                    const keeper = sorted[0];
                    return (
                      <Card key={group.name} className="border-amber-200">
                        <CardContent className="py-3 px-4">
                          <p className="text-sm font-semibold mb-2">"{group.name}" — {group.count} records</p>
                          <div className="space-y-1.5">
                            {sorted.map((item) => {
                              const isKeeper = item.id === keeper.id;
                              return (
                                <div key={item.id} className={`flex items-center justify-between rounded px-3 py-2 text-xs ${isKeeper ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid={`dup-company-${item.id}`}>
                                  <div className="flex items-center gap-3">
                                    <Badge className={isKeeper ? 'bg-green-100 text-green-800 text-[10px]' : 'bg-red-100 text-red-800 text-[10px]'}>
                                      {isKeeper ? 'KEEP' : 'DELETE'}
                                    </Badge>
                                    <span className="font-medium">{item.name}</span>
                                    <span className="text-slate-500">{item.company_type || 'N/A'}</span>
                                    <span className="text-slate-400">ID: {item.id.substring(0, 8)}...</span>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <span className="text-slate-600">{item.client_count} clients</span>
                                    <span className="text-slate-600">{item.user_count} users</span>
                                    <span className="text-slate-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : '?'}</span>
                                    {!isKeeper && (
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        className="h-6 text-[10px] px-2"
                                        disabled={isDeletingDup === item.id}
                                        onClick={() => {
                                          if (window.confirm(`Delete "${item.name}" (${item.client_count} clients, ${item.user_count} users)?\n\nClients & users will be reassigned to the keeper record.`)) {
                                            deleteDuplicateCompany(item.id, keeper.id);
                                          }
                                        }}
                                        data-testid={`delete-dup-company-${item.id}`}
                                      >
                                        {isDeletingDup === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                                        Delete & Merge
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Client Duplicates */}
              {duplicates?.results?.clients?.length > 0 && (
                <div className="space-y-3" data-testid="client-duplicates">
                  <h3 className="text-sm font-semibold flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                    Duplicate Clients ({duplicates.results.clients.length} groups)
                  </h3>
                  {duplicates.results.clients.map((group) => (
                    <Card key={group.email} className="border-amber-200">
                      <CardContent className="py-3 px-4">
                        <p className="text-sm font-semibold mb-2">{group.email} — {group.count} records</p>
                        <div className="space-y-1.5">
                          {group.items.map((item, idx) => (
                            <div key={item.id} className={`flex items-center justify-between rounded px-3 py-2 text-xs ${idx === 0 ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                              <div className="flex items-center gap-3">
                                {idx === 0 && <Badge className="bg-green-100 text-green-800 text-[10px]">OLDEST</Badge>}
                                <span className="font-medium cursor-pointer hover:text-blue-600 hover:underline" onClick={() => navigate(`/clients/${item.id}`)}>{item.full_name || 'N/A'}</span>
                                <span className="text-slate-500">{item.company_name || 'N/A'}</span>
                                <span className="text-slate-400">{item.deal_stage || 'N/A'}</span>
                                <span className="text-slate-400">ID: {item.id.substring(0, 8)}...</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-slate-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : '?'}</span>
                                {idx > 0 && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="h-6 text-[10px] px-2"
                                    disabled={isDeletingDup === item.id}
                                    onClick={() => {
                                      if (window.confirm(`Delete client "${item.full_name}" (${item.deal_stage})? This cannot be undone.`)) {
                                        deleteDuplicateClient(item.id);
                                      }
                                    }}
                                  >
                                    {isDeletingDup === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* User Duplicates */}
              {duplicates?.results?.users?.length > 0 && (
                <div className="space-y-3" data-testid="user-duplicates">
                  <h3 className="text-sm font-semibold flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                    Duplicate Users ({duplicates.results.users.length} groups)
                  </h3>
                  {duplicates.results.users.map((group) => (
                    <Card key={group.email} className="border-amber-200">
                      <CardContent className="py-3 px-4">
                        <p className="text-sm font-semibold mb-2">{group.email} — {group.count} records</p>
                        <div className="space-y-1.5">
                          {group.items.map((item, idx) => (
                            <div key={item.id} className={`flex items-center justify-between rounded px-3 py-2 text-xs ${idx === 0 ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                              <div className="flex items-center gap-3">
                                {idx === 0 && <Badge className="bg-green-100 text-green-800 text-[10px]">KEEP</Badge>}
                                <span className="font-medium">{item.full_name || 'N/A'}</span>
                                <span className="text-slate-500">{item.role || 'N/A'}</span>
                                <span className="text-slate-400">ID: {item.id.substring(0, 8)}...</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-slate-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : '?'}</span>
                                {idx > 0 && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="h-6 text-[10px] px-2"
                                    disabled={isDeletingDup === item.id}
                                    onClick={() => {
                                      if (window.confirm(`Delete user "${item.full_name}" (${item.role})? This cannot be undone.`)) {
                                        deleteDuplicateUser(item.id);
                                      }
                                    }}
                                  >
                                    {isDeletingDup === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DATA INTEGRITY TAB */}
        <TabsContent value="data-integrity" className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

      {/* v3.21-1688 · Funding SSOT explanation — replaces the legacy
          vendor_payment_received_date rule with the canonical v1686
          classifier.  This page is a diagnostic VIEW layer over
          `classify_funding_evidence`; identical classifications
          appear on the Funding Evidence & SSOT Diagnostics page. */}
      <Card className="border-blue-200 bg-blue-50" data-testid="funding-ssot-banner">
        <CardHeader className="pb-3">
          <CardTitle className="text-blue-900 text-sm flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Funding SSOT (v3.21-1686 canonical rules)
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-900 space-y-1">
          <p>
            <strong>Authoritative funded-dollar evidence</strong> = only
            {' '}<code className="bg-blue-100 px-1 rounded">funded_amount &gt; 0</code>{' '}
            OR a <code className="bg-blue-100 px-1 rounded">funding_sources[]</code> array
            carrying real disbursed dollars.
          </p>
          <p>
            <strong>Not authoritative funding evidence</strong>:
            {' '}<code className="bg-blue-100 px-1 rounded">deal_amount</code>,
            {' '}<code className="bg-blue-100 px-1 rounded">approved_amount</code>,
            {' '}<code className="bg-blue-100 px-1 rounded">requested_amount</code>,
            {' '}offer / financing-approval amounts, bare
            {' '}<code className="bg-blue-100 px-1 rounded">funded_at</code> timestamp, or
            {' '}<code className="bg-blue-100 px-1 rounded">vendor_payment_received_date</code>
            {' '}(that is a partner/vendor payment marker, not client funding).
          </p>
          <p>
            Client funding and settlement
            (<code className="bg-blue-100 px-1 rounded">paid_in_full</code> /
            {' '}<code className="bg-blue-100 px-1 rounded">all_parties_settled</code>)
            remain separate financial states.  This screen is diagnostic-only —
            it never rewrites financial fields.  Corrections must occur
            through the authoritative funding workflow.
          </p>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button 
          onClick={runAudit} 
          disabled={isAuditing || isFixing}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="run-audit-btn"
        >
          {isAuditing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running Audit...
            </>
          ) : (
            <>
              <Search className="h-4 w-4 mr-2" />
              Run Audit
            </>
          )}
        </Button>

        {/* v3.21-1688 · Legacy "Fix Data (N records)" bulk writer
            REMOVED.  This diagnostic surface no longer manufactures
            financial truth — corrections must occur through the
            authoritative funding workflow. */}

        {postFixAudit && (
          <Button 
            onClick={runAudit} 
            variant="outline"
            data-testid="re-audit-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Re-Run Audit
          </Button>
        )}
      </div>

      {/* Audit Results */}
      {auditResult && (
        <div className="space-y-4">
          {/* Status Card */}
          <Card className={isClean ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardHeader className="pb-3">
              <CardTitle className={`flex items-center gap-2 ${isClean ? 'text-green-800' : 'text-red-800'}`}>
                {isClean ? (
                  <>
                    <CheckCircle2 className="h-5 w-5" />
                    Data Integrity Status: CLEAN
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5" />
                    Data Integrity Status: NEEDS FIX
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className={`text-2xl font-bold ${hasIssues ? 'text-red-600' : 'text-green-600'}`}>
                    {auditResult.audit_summary.incorrectly_marked_as_funded}
                  </p>
                  <p className="text-slate-600">Issues Found</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-blue-600">
                    {auditResult.audit_summary.valid_funded_deals}
                  </p>
                  <p className="text-slate-600">Valid Funded Deals</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-slate-700">
                    {auditResult.total_affected}
                  </p>
                  <p className="text-slate-600">Total Affected</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Validation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-600" />
                Financial Validation (Expected Values)
              </CardTitle>
              <CardDescription>
                These values represent what Dashboard and Settlement Ledger SHOULD show
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Total Funded Volume</p>
                  <p className="text-xl font-bold text-slate-900">
                    {formatCurrency(auditResult.financial_validation.total_funded_volume_should_be)}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Funded This Month</p>
                  <p className="text-xl font-bold text-slate-900">
                    {formatCurrency(auditResult.financial_validation.funded_this_month_should_be)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {auditResult.financial_validation.funded_this_month_deal_count} deals
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Affected Records */}
          {auditResult.affected_records && auditResult.affected_records.length > 0 && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2 text-red-800">
                  <Users className="h-4 w-4" />
                  Affected Records ({auditResult.affected_records.length})
                </CardTitle>
                <CardDescription>
                  These records have incorrect funding status and need to be fixed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {auditResult.affected_records.map((record, idx) => (
                    <div 
                      key={record.id || idx}
                      className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100"
                      data-testid={`affected-record-${idx}`}
                    >
                      <div>
                        <p className="font-medium text-slate-900 cursor-pointer hover:text-blue-600 hover:underline" onClick={() => record.id && navigate(`/clients/${record.id}`)}>{record.full_name || 'Unknown'}</p>
                        <p className="text-xs text-slate-600">
                          Deal Amount: {formatCurrency(record.deal_amount)} | 
                          Stage: {record.deal_stage || 'N/A'} | 
                          Status: {record.funding_status || 'N/A'}
                        </p>
                      </div>
                      <Badge variant="destructive" className="text-xs">
                        {record.issue === 'funded_with_zero_amount' 
                          ? 'Funded with $0 amount' 
                          : 'Funded without payment'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Funded This Month Deals */}
          {auditResult.financial_validation.funded_this_month_deals && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Valid Funded Deals This Month ({auditResult.financial_validation.funded_this_month_deal_count})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {auditResult.financial_validation.funded_this_month_deals.map((deal, idx) => (
                    <div 
                      key={deal.id || idx}
                      className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-100"
                    >
                      <div>
                        <p className="font-medium text-slate-900 cursor-pointer hover:text-blue-600 hover:underline" onClick={() => deal.id && navigate(`/clients/${deal.id}`)}>{deal.full_name}</p>
                        <p className="text-xs text-slate-600">
                          Payment: {deal.funded_at?.slice(0, 10) || 'N/A'}
                        </p>
                      </div>
                      <p className="font-semibold text-green-700">
                        {formatCurrency(deal.funded_amount)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Fix Results */}
      {fixResult && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Fix Applied Successfully
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-blue-600">
                  {fixResult.fix_summary.deal_stage_records_fixed}
                </p>
                <p className="text-slate-600">Deal Stage Fixed</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-blue-600">
                  {fixResult.fix_summary.funding_status_records_fixed}
                </p>
                <p className="text-slate-600">Funding Status Fixed</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-blue-600">
                  {fixResult.fix_summary.zero_amount_funded_fixed}
                </p>
                <p className="text-slate-600">$0 Amount Fixed</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-green-600">
                  {fixResult.fix_summary.total_fixed}
                </p>
                <p className="text-slate-600">Total Fixed</p>
              </div>
            </div>
            <p className="text-sm text-blue-800">
              Performed by: {fixResult.performed_by} at {fixResult.timestamp?.slice(0, 19)}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Post-Fix Audit Results */}
      {postFixAudit && (
        <Card className={postFixAudit.audit_summary.data_integrity_status === 'CLEAN' 
          ? 'border-green-300 bg-green-100' 
          : 'border-yellow-300 bg-yellow-100'
        }>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {postFixAudit.audit_summary.data_integrity_status === 'CLEAN' ? (
                <>
                  <ShieldCheck className="h-5 w-5 text-green-700" />
                  <span className="text-green-800">Post-Fix Validation: CLEAN</span>
                </>
              ) : (
                <>
                  <ShieldAlert className="h-5 w-5 text-yellow-700" />
                  <span className="text-yellow-800">Post-Fix Validation: Issues Remain</span>
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <p className="text-slate-600">Remaining Issues</p>
                <p className={`text-2xl font-bold ${postFixAudit.audit_summary.incorrectly_marked_as_funded === 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {postFixAudit.audit_summary.incorrectly_marked_as_funded}
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <p className="text-slate-600">Valid Funded Deals</p>
                <p className="text-2xl font-bold text-blue-600">
                  {postFixAudit.audit_summary.valid_funded_deals}
                </p>
              </div>
            </div>
            {postFixAudit.audit_summary.data_integrity_status === 'CLEAN' && (
              <Alert className="mt-4 bg-green-200 border-green-300">
                <CheckCircle2 className="h-4 w-4 text-green-700" />
                <AlertTitle className="text-green-800">All Issues Resolved</AlertTitle>
                <AlertDescription className="text-green-700">
                  Dashboard and Settlement Ledger should now show matching values.
                  Funded This Month: {formatCurrency(postFixAudit.financial_validation.funded_this_month_should_be)}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      {!auditResult && (
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Database className="h-12 w-12 text-slate-400 mx-auto" />
              <div>
                <h3 className="font-medium text-slate-700">How to Use</h3>
                <ol className="text-sm text-slate-600 mt-2 space-y-1 text-left max-w-md mx-auto">
                  <li>1. Click <strong>"Run Audit"</strong> to identify data integrity issues</li>
                  <li>2. Review affected records (e.g., Cindy Bailey with $0 marked as funded)</li>
                  <li>3. Click <strong>"Fix Data"</strong> to correct all issues</li>
                  <li>4. Verify post-fix audit shows <strong>CLEAN</strong> status</li>
                  <li>5. Check Dashboard and Settlement Ledger values match</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        {/* UNFUND DEALS TAB (v3.14-82) */}
        <TabsContent value="unfund-deals" className="space-y-4">
          {/* Error Alert */}
          {unfundError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{unfundError}</AlertDescription>
            </Alert>
          )}

          {/* Success Alert */}
          {unfundResult && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Deals Unfunded Successfully</AlertTitle>
              <AlertDescription className="text-green-700">
                {unfundResult.unfunded_count} deal(s) have been unfunded. They will no longer show as "Funded" in Settlement Ledger.
                {unfundResult.not_found?.length > 0 && (
                  <span className="block mt-1 text-amber-700">
                    Names not found: {unfundResult.not_found.join(', ')}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Quick Unfund by Name */}
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2 text-orange-800">
                <XCircle className="h-4 w-4" />
                Quick Unfund by Name
              </CardTitle>
              <CardDescription>
                Enter client names (comma-separated) to unfund. Example: Sandra Dierdorf, James Sexton
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Sandra Dierdorf, James Sexton, Cindy Bailey"
                  value={unfundNames}
                  onChange={(e) => setUnfundNames(e.target.value)}
                  className="flex-1"
                  data-testid="unfund-names-input"
                />
                <Button 
                  onClick={unfundByNames}
                  disabled={isUnfunding || !unfundNames.trim()}
                  variant="destructive"
                  data-testid="unfund-by-names-btn"
                >
                  {isUnfunding ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Unfunding...
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Unfund by Name
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Load All Funded Deals */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Search className="h-4 w-4 text-blue-600" />
                View All Funded Deals
              </CardTitle>
              <CardDescription>
                Load all deals with authoritative funded-dollar evidence (funded_amount &gt; 0 OR qualifying disbursement in funding_sources). Settlement status is shown separately per row.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                onClick={loadFundedDeals}
                disabled={isLoadingFunded}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="load-funded-btn"
              >
                {isLoadingFunded ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Load Funded Deals
                  </>
                )}
              </Button>

              {fundedDeals && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                      Found <strong>{fundedDeals.total_funded_deals}</strong> deals marked as funded
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllDeals}>
                        {selectedDeals.length === fundedDeals.funded_deals?.length ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        disabled={selectedDeals.length === 0 || isUnfunding}
                        onClick={unfundSelectedDeals}
                        data-testid="unfund-selected-btn"
                      >
                        {isUnfunding ? 'Unfunding...' : `Unfund Selected (${selectedDeals.length})`}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {fundedDeals.funded_deals?.map((deal, idx) => (
                      <div 
                        key={deal.id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedDeals.includes(deal.id) 
                            ? 'bg-red-50 border-red-200' 
                            : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                        }`}
                        onClick={() => toggleDealSelection(deal.id)}
                      >
                        <div className="flex items-center gap-3">
                          <input 
                            type="checkbox" 
                            checked={selectedDeals.includes(deal.id)}
                            onChange={() => {}}
                            className="h-4 w-4"
                          />
                          <div>
                            <p className="font-medium text-slate-900 cursor-pointer hover:text-blue-600 hover:underline" onClick={() => deal.id && navigate(`/clients/${deal.id}`)}>{deal.full_name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">
                              Company: {deal.company_name} | 
                              Deal: {formatCurrency(deal.deal_amount)} | 
                              Funded: {deal.vendor_payment_received_date?.slice(0, 10)}
                            </p>
                          </div>
                        </div>
                        <Badge variant={deal.deal_amount > 0 ? "default" : "destructive"}>
                          {deal.deal_amount > 0 ? `$${deal.deal_amount?.toLocaleString()}` : '$0 Deal'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!fundedDeals && (
                <p className="text-sm text-slate-500">
                  Click "Load Funded Deals" to see all deals currently marked as funded.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMMISSION AUDIT TAB (v3.14-86) */}
        <TabsContent value="commission-audit" className="space-y-4">
          {commissionError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{commissionError}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                Commission Data Integrity Audit
              </CardTitle>
              <CardDescription>
                Audit all commissions to find orphaned records (commissions without valid client records).
                Rule: Every commission MUST link to a valid client.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Button
                  onClick={async () => {
                    setIsAuditingCommissions(true);
                    setCommissionError(null);
                    setCommissionAudit(null);
                    try {
                      const response = await api.get('/admin/audit-commission-integrity');
                      setCommissionAudit(response.data);
                    } catch (err) {
                      setCommissionError(err.response?.data?.detail || 'Failed to audit commissions');
                    } finally {
                      setIsAuditingCommissions(false);
                    }
                  }}
                  disabled={isAuditingCommissions}
                  data-testid="audit-commissions-btn"
                >
                  {isAuditingCommissions ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Auditing...</>
                  ) : (
                    <><Search className="h-4 w-4 mr-2" />Audit Commissions</>
                  )}
                </Button>
                
                {commissionAudit && (commissionAudit.orphaned_count || 0) > 0 && (
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      setIsFixingCommissions(true);
                      setCommissionError(null);
                      try {
                        const response = await api.post('/admin/fix-orphaned-commissions');
                        setCommissionFixResult(response.data);
                        // Re-run audit
                        const auditResponse = await api.get('/admin/audit-commission-integrity');
                        setCommissionAudit(auditResponse.data);
                      } catch (err) {
                        setCommissionError(err.response?.data?.detail || 'Failed to fix commissions');
                      } finally {
                        setIsFixingCommissions(false);
                      }
                    }}
                    disabled={isFixingCommissions}
                    data-testid="fix-commissions-btn"
                  >
                    {isFixingCommissions ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Fixing...</>
                    ) : (
                      <><Wrench className="h-4 w-4 mr-2" />Fix Orphaned Commissions</>
                    )}
                  </Button>
                )}
              </div>

              {commissionAudit && (() => {
                // v3.15-1043: HARD RULE — only two states. Red = real errors,
                // Green = clean. No amber, no informational, no audit theater.
                // The previous "not_funded" metric is fully ignored; it was a
                // legacy informational state that mis-classified legitimate
                // pre-SSOT data as a problem.
                const orphaned = commissionAudit.orphaned_count || 0;
                const isClean  = orphaned === 0;
                const cls = isClean ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
                const iconCls = isClean ? 'text-green-600' : 'text-red-600';
                const Icon = isClean ? CheckCircle2 : XCircle;
                const title = isClean ? 'ALL COMMISSIONS VALID' : 'ORPHANED COMMISSIONS FOUND';
                return (
                  <div className="mt-4 space-y-4" data-testid="commission-audit-results">
                    <div className={`p-4 rounded-lg border ${cls}`} data-testid={`commission-audit-banner-${isClean ? 'green' : 'red'}`}>
                      <div className="flex items-center gap-2">
                        <Icon className={`h-5 w-5 ${iconCls}`} />
                        <span className="font-semibold text-lg">{title}</span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                        <div><strong>Total Commissions:</strong> {commissionAudit.total_commissions}</div>
                        <div><strong>Orphaned:</strong> {orphaned}</div>
                      </div>
                    </div>

                    {commissionAudit.orphaned_commissions && commissionAudit.orphaned_commissions.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-red-600">Orphaned Commissions:</h4>
                        <div className="max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Commission ID</th>
                                <th className="text-left p-2">Client ID</th>
                                <th className="text-left p-2">Type</th>
                                <th className="text-left p-2">Amount</th>
                                <th className="text-left p-2">Issue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {commissionAudit.orphaned_commissions.map((oc, idx) => (
                                <tr key={oc.commission_id || `orphan-${idx}`} className="border-b">
                                  <td className="p-2 font-mono text-xs">{oc.commission_id?.substring(0, 8)}...</td>
                                  <td className="p-2 font-mono text-xs">{oc.client_id?.substring(0, 8) || 'N/A'}...</td>
                                  <td className="p-2">{oc.commission_type}</td>
                                  <td className="p-2">${oc.amount?.toFixed(2)}</td>
                                  <td className="p-2">
                                    <Badge variant={oc.issue === 'NO_CLIENT_ID' ? 'destructive' : 'secondary'}>
                                      {oc.issue}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {commissionFixResult && (
                <Alert className="bg-blue-50 border-blue-200">
                  <Wrench className="h-4 w-4 text-blue-600" />
                  <AlertTitle>Fix Results</AlertTitle>
                  <AlertDescription>
                    <p>Fixed: {commissionFixResult.fixed_count} | Deleted: {commissionFixResult.deleted_count}</p>
                    {commissionFixResult.fixed?.length > 0 && (
                      <div className="mt-2">
                        <strong>Created Clients:</strong>
                        <ul className="list-disc list-inside">
                          {commissionFixResult.fixed.map((f, i) => (
                            <li key={f.client_name || `fix-${i}`}>{f.client_name} (from application)</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FINANCIAL AUDIT TAB (v3.14-87) */}
        <TabsContent value="financial-audit" className="space-y-4">
          {/* v3.18-1312 — Financial Integrity Resolution Center (Refined).
              Removed silent smart-repair sync buttons.  Added exact
              per-event mismatch cards, diagnostic confidence, side-by-side
              diff modal, and a "Reviewed" status for investigations that
              require no repair. */}
          <FinancialIntegrityCenter />
        </TabsContent>

        {/* BACKFILL DATA TAB (v3.14-84) */}
        <TabsContent value="backfill" className="space-y-4">
          {/* Error Alert */}
          {backfillError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{backfillError}</AlertDescription>
            </Alert>
          )}

          {/* Success Alert */}
          {backfillResult && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Backfill Complete</AlertTitle>
              <AlertDescription className="text-green-700">
                Scanned {backfillResult.clients_scanned} clients, updated {backfillResult.clients_updated} records with:
                <ul className="mt-1 ml-4 list-disc">
                  <li>Credit App Confirmation #</li>
                  <li>Application ID</li>
                  <li>Loan Requested</li>
                  <li>Loan Purpose</li>
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Card className="border-purple-200">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2 text-purple-800">
                <RefreshCw className="h-4 w-4" />
                Backfill Missing Intake Data
              </CardTitle>
              <CardDescription>
                Existing clients created before v3.14-84 may be missing intake data.
                This will scan all clients and populate missing fields from their applications.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg text-sm space-y-2">
                <p className="font-medium text-purple-800">This will update clients missing:</p>
                <ul className="text-purple-700 ml-4 list-disc">
                  <li><code>credit_app_confirmation_number</code> (Credit App Confirmation #)</li>
                  <li><code>application_id</code> (Application ID)</li>
                  <li><code>loan_requested</code> (Loan Requested)</li>
                  <li><code>loan_purpose</code> (Loan Purpose)</li>
                </ul>
              </div>
              <Button 
                onClick={runBackfill}
                disabled={isBackfilling}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="run-backfill-btn"
              >
                {isBackfilling ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Backfilling...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Run Backfill
                  </>
                )}
              </Button>

              {backfillResult?.updated_clients?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Updated Clients:</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {backfillResult.updated_clients.map((client, idx) => (
                      <div key={client.client_id || idx} className="p-2 bg-green-50 rounded border border-green-100 text-sm">
                        <p className="font-medium cursor-pointer hover:text-blue-600 hover:underline" onClick={() => client.client_id && navigate(`/clients/${client.client_id}`)}>{client.full_name}</p>
                        <p className="text-xs text-slate-500">
                          Updated: {client.fields_updated?.join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMMUNICATIONS TAB (v3.14-85) */}
        <TabsContent value="communications" className="space-y-4">
          {commsError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{commsError}</AlertDescription>
            </Alert>
          )}

          {/* Communications Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5 text-blue-600" />
                Communications Service Status
              </CardTitle>
              <CardDescription>
                Check SMTP (Email) and Twilio (SMS) configuration status
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={async () => {
                  setIsLoadingCommsStatus(true);
                  setCommsError(null);
                  try {
                    const response = await api.get('/admin/communications-status');
                    setCommsStatus(response.data);
                  } catch (err) {
                    setCommsError(err.response?.data?.detail || 'Failed to load status');
                  } finally {
                    setIsLoadingCommsStatus(false);
                  }
                }}
                disabled={isLoadingCommsStatus}
                data-testid="check-comms-status-btn"
              >
                {isLoadingCommsStatus ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Checking...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" />Check Status</>
                )}
              </Button>

              {commsStatus && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {/* SMTP Status */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="h-4 w-4" />
                      <span className="font-semibold">SMTP (Email)</span>
                      {commsStatus.smtp.configured ? (
                        <Badge className="bg-green-100 text-green-700">Configured</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Not Configured</Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p><strong>Host:</strong> {commsStatus.smtp.host}:{commsStatus.smtp.port}</p>
                      <p><strong>Username:</strong> {commsStatus.smtp.username}</p>
                      <p><strong>Sender:</strong> {commsStatus.smtp.sender_email}</p>
                    </div>
                  </div>

                  {/* SMS Status */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="h-4 w-4" />
                      <span className="font-semibold">Twilio (SMS)</span>
                      {commsStatus.sms.configured ? (
                        <Badge className="bg-green-100 text-green-700">Configured</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Not Configured</Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p><strong>From Number:</strong> {commsStatus.sms.from_number}</p>
                      <p><strong>Account SID:</strong> {commsStatus.sms.twilio_account_sid}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Test Email Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-indigo-600" />
                Test Email
              </CardTitle>
              <CardDescription>
                Send a test email to verify SMTP is working
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label htmlFor="test-email">Email Address</Label>
                  <Input
                    id="test-email"
                    type="email"
                    placeholder="test@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    data-testid="test-email-input"
                  />
                </div>
                <Button
                  onClick={async () => {
                    if (!testEmail) {
                      setCommsError('Please enter an email address');
                      return;
                    }
                    setIsSendingTestEmail(true);
                    setEmailTestResult(null);
                    setCommsError(null);
                    try {
                      const response = await api.post('/admin/test-email', { to_email: testEmail });
                      setEmailTestResult(response.data);
                    } catch (err) {
                      setEmailTestResult({ success: false, error_message: err.response?.data?.detail || 'Failed to send' });
                    } finally {
                      setIsSendingTestEmail(false);
                    }
                  }}
                  disabled={isSendingTestEmail}
                  data-testid="send-test-email-btn"
                >
                  {isSendingTestEmail ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" />Send Test Email</>
                  )}
                </Button>
              </div>

              {emailTestResult && (
                <Alert className={emailTestResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                  {emailTestResult.success ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                  <AlertTitle>{emailTestResult.success ? 'Email Sent!' : 'Email Failed'}</AlertTitle>
                  <AlertDescription>
                    {emailTestResult.success ? (
                      <p>Test email sent to {emailTestResult.to_email}</p>
                    ) : (
                      <p>Error: {emailTestResult.error_message}</p>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Test SMS Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-green-600" />
                Test SMS
              </CardTitle>
              <CardDescription>
                Send a test SMS to verify Twilio is working
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label htmlFor="test-phone">Phone Number</Label>
                  <Input
                    id="test-phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    data-testid="test-phone-input"
                  />
                </div>
                <Button
                  onClick={async () => {
                    if (!testPhone) {
                      setCommsError('Please enter a phone number');
                      return;
                    }
                    setIsSendingTestSms(true);
                    setSmsTestResult(null);
                    setCommsError(null);
                    try {
                      const response = await api.post('/admin/test-sms', { phone_number: testPhone });
                      setSmsTestResult(response.data);
                    } catch (err) {
                      setSmsTestResult({ success: false, error_message: err.response?.data?.detail || 'Failed to send' });
                    } finally {
                      setIsSendingTestSms(false);
                    }
                  }}
                  disabled={isSendingTestSms}
                  data-testid="send-test-sms-btn"
                >
                  {isSendingTestSms ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" />Send Test SMS</>
                  )}
                </Button>
              </div>

              {smsTestResult && (
                <Alert className={smsTestResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                  {smsTestResult.success ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                  <AlertTitle>{smsTestResult.success ? 'SMS Sent!' : 'SMS Failed'}</AlertTitle>
                  <AlertDescription>
                    {smsTestResult.success ? (
                      <div>
                        <p>Test SMS sent to {smsTestResult.to_phone}</p>
                        <p className="text-xs text-slate-500 mt-1">Twilio SID: {smsTestResult.twilio_sid}</p>
                      </div>
                    ) : (
                      <div>
                        <p>Error: {smsTestResult.error_message}</p>
                        {!smsTestResult.configured && (
                          <p className="text-xs text-red-500 mt-1">Twilio is not configured. Check TWILIO_* environment variables.</p>
                        )}
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CLIENT MANAGEMENT TAB - v3.14-92 */}
        <TabsContent value="client-management" className="space-y-4">
          {/* Client Error Alert */}
          {clientError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{clientError}</AlertDescription>
            </Alert>
          )}

          {/* Delete Result */}
          {deleteResult && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Deletion Complete</AlertTitle>
              <AlertDescription className="text-green-700">
                Deleted {deleteResult.summary?.clients} clients, {deleteResult.summary?.ledger_entries} ledger entries, 
                {deleteResult.summary?.commissions} commissions, {deleteResult.summary?.activities} activities
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ListFilter className="h-5 w-5 text-red-600" />
                Client/Deal Management
              </CardTitle>
              <CardDescription>
                Search, review, and delete clients/deals. Use this tool to remove test/demo data.
                <br />
                <strong className="text-red-600">WARNING:</strong> Deletion is permanent and includes all related data (ledger, commissions, activities).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search and Load */}
              <div className="flex gap-2">
                <Input
                  placeholder="Search by name, email, phone, or ID..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadClients(1, clientSearch)}
                  className="flex-1"
                />
                <Button 
                  onClick={() => loadClients(1, clientSearch)}
                  disabled={isLoadingClients}
                >
                  {isLoadingClients ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span className="ml-2">Search</span>
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => { setClientSearch(''); loadClients(1, ''); }}
                  disabled={isLoadingClients}
                >
                  <RefreshCw className="h-4 w-4" />
                  <span className="ml-2">Load All</span>
                </Button>
              </div>

              {/* Selection Actions */}
              {clientList?.clients?.length > 0 && (
                <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                  <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                      {selectedClients.length === clientList.clients.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <span className="text-sm text-slate-600">
                      {selectedClients.length} of {clientList.total} selected
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={previewDelete}
                      disabled={selectedClients.length === 0 || isDeleting}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview Deletion
                    </Button>
                    {deletePreview && (
                      <Button 
                        variant="destructive"
                        size="sm"
                        onClick={confirmDelete}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-1" />
                        )}
                        Confirm Delete ({selectedClients.length})
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Delete Preview */}
              {deletePreview && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Deletion Preview - Review Before Confirming
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {deletePreview.clients_to_delete?.map((c) => (
                        <div key={c.id} className="flex justify-between text-sm border-b border-amber-200 pb-1">
                          <span className="font-medium">{c.name || 'No Name'}</span>
                          <span className="text-amber-700">
                            ${c.deal_amount?.toLocaleString() || 0} | {c.ledger_entries} ledger | {c.commissions} comm | {c.activities} acts
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Client List Table */}
              {clientList?.clients?.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="w-10 p-2"></th>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-right p-2">Deal Amount</th>
                          <th className="text-left p-2">Stage</th>
                          <th className="text-left p-2">Payment Type</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-left p-2">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientList.clients.map((client) => (
                          <tr 
                            key={client.id} 
                            className={`border-b hover:bg-slate-50 ${selectedClients.includes(client.id) ? 'bg-red-50' : ''}`}
                          >
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={selectedClients.includes(client.id)}
                                onChange={() => handleClientSelect(client.id)}
                                className="w-4 h-4"
                              />
                            </td>
                            <td className="p-2 font-medium">{client.name || 'No Name'}</td>
                            <td className="p-2 text-slate-600">{client.email || '-'}</td>
                            <td className="p-2 text-right">{formatCurrency(client.deal_amount)}</td>
                            <td className="p-2">
                              <Badge variant="outline" className="text-xs">
                                {client.deal_stage || 'N/A'}
                              </Badge>
                            </td>
                            <td className="p-2">
                              {client.payment_type ? (
                                <Badge variant="secondary" className="text-xs">
                                  {client.payment_type}
                                </Badge>
                              ) : (
                                <Badge variant="destructive" className="text-xs">
                                  Missing
                                </Badge>
                              )}
                            </td>
                            <td className="p-2">
                              {client.is_calculable ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Valid</Badge>
                              ) : (
                                <Badge variant="destructive" className="text-xs">
                                  {client.missing_fields?.join(', ') || 'Invalid'}
                                </Badge>
                              )}
                            </td>
                            <td className="p-2 text-xs text-slate-500">
                              {client.created_at ? new Date(client.created_at).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 border-t">
                    <span className="text-sm text-slate-600">
                      Page {clientList.page} of {clientList.pages} ({clientList.total} total clients)
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadClients(clientPage - 1, clientSearch)}
                        disabled={clientPage <= 1 || isLoadingClients}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadClients(clientPage + 1, clientSearch)}
                        disabled={clientPage >= clientList.pages || isLoadingClients}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              ) : clientList ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No clients found matching your search.</p>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <ListFilter className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Click "Load All" or search to view clients.</p>
                  <p className="text-xs mt-1">Use this tool to identify and remove test/demo data.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* USER MANAGEMENT TAB */}
        <TabsContent value="user-management" className="space-y-4">
          {/* User Error Alert */}
          {userError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{userError}</AlertDescription>
            </Alert>
          )}

          {/* Password Reset Success */}
          {passwordResetResult && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Password Reset Successful</AlertTitle>
              <AlertDescription className="text-green-700">
                Password has been reset for <strong>{passwordResetResult.user_email}</strong> ({passwordResetResult.user_name}).
                User can now log in with the new password.
              </AlertDescription>
            </Alert>
          )}

          {/* User Verification Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-blue-600" />
                Verify User & Reset Password
              </CardTitle>
              <CardDescription>
                Check if a user exists in the system and reset their password if needed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="userEmail">User Email</Label>
                  <Input
                    id="userEmail"
                    type="email"
                    placeholder="jessica.c@prestigioustravelclub.org"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={verifyUser}
                  disabled={isVerifyingUser || !userEmail.trim()}
                  variant="outline"
                  data-testid="verify-user-btn"
                >
                  {isVerifyingUser ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Verify User
                    </>
                  )}
                </Button>
                <Button 
                  onClick={resetPassword}
                  disabled={isResettingPassword || !userEmail.trim() || !newPassword.trim()}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="reset-password-btn"
                >
                  {isResettingPassword ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Key className="h-4 w-4 mr-2" />
                      Reset Password
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* User Verification Result */}
          {userVerifyResult && (
            <Card className={userVerifyResult.exists ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-sm flex items-center gap-2 ${userVerifyResult.exists ? 'text-green-800' : 'text-red-800'}`}>
                  {userVerifyResult.exists ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      User Found
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      User Not Found
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {userVerifyResult.exists ? (
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-slate-600">Name</p>
                        <p className="font-medium">{userVerifyResult.user.full_name}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Email</p>
                        <p className="font-medium">{userVerifyResult.user.email}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Role</p>
                        <p className="font-medium">{userVerifyResult.user.role}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Active</p>
                        <Badge variant={userVerifyResult.user.is_active ? "default" : "destructive"}>
                          {userVerifyResult.user.is_active ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-slate-600">Company</p>
                        <p className="font-medium">{userVerifyResult.company?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Has Password</p>
                        <Badge variant={userVerifyResult.has_password_hash ? "default" : "destructive"}>
                          {userVerifyResult.has_password_hash ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                    {userVerifyResult.user.password_reset_at && (
                      <p className="text-xs text-slate-500 mt-2">
                        Last password reset: {userVerifyResult.user.password_reset_at?.slice(0, 19)} by {userVerifyResult.user.password_reset_by}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-red-700">{userVerifyResult.message}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* v3.14-101: User Lifecycle Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-600" />
                User Lifecycle Management
              </CardTitle>
              <CardDescription>
                View all users and manage their account status (deactivate, archive, reactivate, delete)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Load Users Button */}
              <div className="flex gap-2">
                <Button 
                  onClick={() => loadUsers(true)}
                  disabled={isLoadingUsers}
                >
                  {isLoadingUsers ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Load All Users
                </Button>
              </div>

              {/* Action Result */}
              {userActionResult && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">{userActionResult.message}</AlertTitle>
                  <AlertDescription className="text-green-700">
                    User: {userActionResult.user_email} → Status: {userActionResult.new_status || 'Deleted'}
                  </AlertDescription>
                </Alert>
              )}

              {/* User List Table */}
              {userList && userList.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Role</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-left p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userList.map((u) => (
                          <tr key={u.id} className="border-b hover:bg-slate-50">
                            <td className="p-2 font-medium">{u.full_name}</td>
                            <td className="p-2 text-slate-600">{u.email}</td>
                            <td className="p-2">
                              <Badge variant="outline" className="text-xs">
                                {u.role}
                              </Badge>
                            </td>
                            <td className="p-2">
                              <Badge 
                                className={`text-xs ${
                                  (u.user_status || 'active') === 'active' ? 'bg-green-100 text-green-800' :
                                  (u.user_status || 'active') === 'inactive' ? 'bg-amber-100 text-amber-800' :
                                  'bg-slate-100 text-slate-600'
                                }`}
                              >
                                {u.user_status || 'active'}
                              </Badge>
                            </td>
                            <td className="p-2">
                              <div className="flex gap-1">
                                {(u.user_status || 'active') === 'active' ? (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-7"
                                      onClick={() => handleUserAction(u.id, 'deactivate')}
                                      disabled={isPerformingUserAction}
                                    >
                                      Deactivate
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-7"
                                      onClick={() => handleUserAction(u.id, 'archive')}
                                      disabled={isPerformingUserAction}
                                    >
                                      Archive
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-7 border-green-500 text-green-700"
                                      onClick={() => handleUserAction(u.id, 'reactivate')}
                                      disabled={isPerformingUserAction}
                                    >
                                      Reactivate
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      className="text-xs h-7"
                                      onClick={() => {
                                        if (window.confirm(`PERMANENTLY DELETE ${u.email}? This cannot be undone.`)) {
                                          handleUserAction(u.id, 'delete');
                                        }
                                      }}
                                      disabled={isPerformingUserAction}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : userList ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No users found</p>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Click "Load All Users" to view user accounts</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Instructions */}
          {!userVerifyResult && !passwordResetResult && (
            <Card className="bg-slate-50">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <Users className="h-12 w-12 text-slate-400 mx-auto" />
                  <div>
                    <h3 className="font-medium text-slate-700">User Management</h3>
                    <ol className="text-sm text-slate-600 mt-2 space-y-1 text-left max-w-md mx-auto">
                      <li>1. Enter user email (e.g., jessica.c@prestigioustravelclub.org)</li>
                      <li>2. Click <strong>"Verify User"</strong> to check if user exists</li>
                      <li>3. If user exists, enter a new password</li>
                      <li>4. Click <strong>"Reset Password"</strong> to update</li>
                      <li>5. User can immediately log in with the new password</li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* v3.14-304: TEST DATA REMOVER TAB */}
        <TabsContent value="test-data" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-500" />
                Test Data Remover
              </CardTitle>
              <CardDescription>
                Identify and remove test records. A record is flagged as test data if: email contains test/example/dummy, name contains test/demo, phone is 1234567890/5555555555/0000000000, or manually flagged (is_test=true). Funded and confirmed deals are always protected.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Scan Button */}
              <Button
                onClick={async () => {
                  setIsLoadingTestData(true);
                  setTestDataError(null);
                  setTestDataResult(null);
                  setTestDataPreview(null);
                  setSelectedTestIds([]);
                  setTestDataConfirmation('');
                  try {
                    const res = await api.get('/admin/test-data/preview');
                    setTestDataPreview(res.data);
                    setSelectedTestIds(res.data.test_records.map(r => r.id));
                  } catch (err) {
                    setTestDataError(err.response?.data?.detail || 'Failed to scan for test data');
                  } finally {
                    setIsLoadingTestData(false);
                  }
                }}
                disabled={isLoadingTestData}
                data-testid="scan-test-data-btn"
              >
                {isLoadingTestData ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                Scan for Test Data
              </Button>

              {testDataError && (
                <Alert variant="destructive"><AlertDescription>{testDataError}</AlertDescription></Alert>
              )}

              {testDataResult && (
                <Alert><AlertDescription>{testDataResult.message} ({testDataResult.deleted_count} removed at {new Date(testDataResult.timestamp).toLocaleString()})</AlertDescription></Alert>
              )}

              {/* Preview Table */}
              {testDataPreview && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-700">
                      Found <Badge variant="destructive">{testDataPreview.count}</Badge> test record(s)
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs"
                      onClick={() => {
                        if (selectedTestIds.length === testDataPreview.test_records.length) {
                          setSelectedTestIds([]);
                        } else {
                          setSelectedTestIds(testDataPreview.test_records.map(r => r.id));
                        }
                      }}
                      data-testid="toggle-select-all-test"
                    >
                      {selectedTestIds.length === testDataPreview.test_records.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  {testDataPreview.count === 0 ? (
                    <Alert><AlertDescription>No test data found. Database is clean.</AlertDescription></Alert>
                  ) : (
                    <>
                      <div className="max-h-[300px] overflow-y-auto border rounded">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              <th className="px-2 py-1.5 text-left w-8"></th>
                              <th className="px-2 py-1.5 text-left">Name</th>
                              <th className="px-2 py-1.5 text-left">Email</th>
                              <th className="px-2 py-1.5 text-left">Phone</th>
                              <th className="px-2 py-1.5 text-left">Created</th>
                              <th className="px-2 py-1.5 text-left">Flagged</th>
                            </tr>
                          </thead>
                          <tbody>
                            {testDataPreview.test_records.map(rec => (
                              <tr key={rec.id} className="border-t hover:bg-slate-50">
                                <td className="px-2 py-1">
                                  <input
                                    type="checkbox"
                                    checked={selectedTestIds.includes(rec.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedTestIds(prev => [...prev, rec.id]);
                                      } else {
                                        setSelectedTestIds(prev => prev.filter(id => id !== rec.id));
                                      }
                                    }}
                                    data-testid={`test-data-checkbox-${rec.id}`}
                                  />
                                </td>
                                <td className="px-2 py-1 text-slate-700 cursor-pointer hover:text-blue-600 hover:underline" onClick={() => navigate(`/clients/${rec.id}`)}>{rec.full_name || '-'}</td>
                                <td className="px-2 py-1 text-slate-500">{rec.email || '-'}</td>
                                <td className="px-2 py-1 text-slate-500">{rec.phone || '-'}</td>
                                <td className="px-2 py-1 text-slate-400">{rec.created_at ? new Date(rec.created_at).toLocaleDateString() : '-'}</td>
                                <td className="px-2 py-1">{rec.is_test ? <Badge variant="destructive" className="text-[10px]">Manual</Badge> : <Badge variant="outline" className="text-[10px]">Auto</Badge>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Confirmation + Delete */}
                      <Separator />
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-red-600">
                          Type <strong>DELETE TEST DATA</strong> to confirm deletion of {selectedTestIds.length} record(s)
                        </Label>
                        <Input
                          value={testDataConfirmation}
                          onChange={(e) => setTestDataConfirmation(e.target.value)}
                          placeholder="DELETE TEST DATA"
                          className="max-w-xs text-sm"
                          data-testid="test-data-confirmation-input"
                        />
                        <Button
                          variant="destructive"
                          onClick={async () => {
                            if (testDataConfirmation !== 'DELETE TEST DATA') return;
                            setIsDeletingTestData(true);
                            setTestDataError(null);
                            try {
                              const res = await api.post('/admin/test-data/delete', {
                                confirmation: testDataConfirmation,
                                client_ids: selectedTestIds,
                              });
                              setTestDataResult(res.data);
                              setTestDataPreview(null);
                              setSelectedTestIds([]);
                              setTestDataConfirmation('');
                            } catch (err) {
                              setTestDataError(err.response?.data?.detail || 'Failed to delete test data');
                            } finally {
                              setIsDeletingTestData(false);
                            }
                          }}
                          disabled={isDeletingTestData || testDataConfirmation !== 'DELETE TEST DATA' || selectedTestIds.length === 0}
                          data-testid="delete-test-data-btn"
                        >
                          {isDeletingTestData ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete {selectedTestIds.length} Test Record(s)
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
